import { hasAccessRequirement, type AccessRequirement } from '@/lib/access-requirements';
import { hasPermissionCode, resolveEffectivePermissionCodes } from '@/lib/permissions';
import 'react';
import { useAuth } from './use-auth';

type PermissionRequirement = string | readonly string[];

function normalizeRequirements(requirement: PermissionRequirement): string[] {
  const values = Array.isArray(requirement) ? requirement : [requirement];
  const requirements: string[] = [];
  for (const item of values) {
    const trimmed = item.trim();
    if (trimmed.length > 0) requirements.push(trimmed);
  }
  return requirements;
}

export function usePermissions() {
  const { user } = useAuth();

  const permissionCodes = resolveEffectivePermissionCodes({
    explicitPermissionCodes: user?.permissionCodes ?? [],
    isSuperAdmin: Boolean(user?.isSuperAdmin),
  });

  const permissionSet = new Set(permissionCodes);
  const permissionCatalog = user?.authorization?.permissionCatalog ?? [];

  const canAll = (requirement: PermissionRequirement) => {
    const requirements = normalizeRequirements(requirement);
    if (requirements.length === 0) return true;
    return requirements.every((code) => hasPermissionCode(permissionSet, code));
  };

  const canAny = (requirement: PermissionRequirement) => {
    const requirements = normalizeRequirements(requirement);
    if (requirements.length === 0) return true;
    return requirements.some((code) => hasPermissionCode(permissionSet, code));
  };

  const can = (requirement: PermissionRequirement) => canAll(requirement);
  const canAccess = (requirement: AccessRequirement) =>
    hasAccessRequirement(permissionSet, requirement, permissionCatalog);
  const canAccessAll = (requirements: readonly AccessRequirement[]) =>
    requirements.every((requirement) => canAccess(requirement));
  const canAccessAny = (requirements: readonly AccessRequirement[]) =>
    requirements.some((requirement) => canAccess(requirement));

  return {
    permissionCodes,
    permissionCatalog,
    can,
    canAll,
    canAny,
    canAccess,
    canAccessAll,
    canAccessAny,
    isSuperAdmin: Boolean(user?.isSuperAdmin),
  };
}
